import { useCallback, useEffect, useState } from "react";

// Y-C2 sess post-launch: "high-contrast" mode (WCAG AAA 7:1) is a separate
// fourth option, kept out of the regular cycle so users won't reach it by
// accident. Opt-in only via Settings.
export type Theme = "dark" | "light" | "auto" | "high-contrast";

const STORAGE_KEY = "storica_theme";

function systemPrefersDark(): boolean {
  return !!window.matchMedia?.("(prefers-color-scheme: dark)").matches;
}

function getPreferred(): Theme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (saved === "dark" || saved === "light" || saved === "auto" || saved === "high-contrast") return saved;
  } catch {
    // silent: localStorage unavailable (private mode / quota)
  }
  return "auto";
}

function apply(theme: Theme) {
  const resolved =
    theme === "auto"
      ? (systemPrefersDark() ? "dark" : "light")
      : theme;
  document.documentElement.setAttribute("data-theme", resolved);
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    const t = getPreferred();
    apply(t);
    return t;
  });
  // Track resolved (effective) theme separately so isDark reacts to OS changes in auto mode
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  useEffect(() => {
    apply(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch {
      // silent: localStorage unavailable (private mode / quota)
    }
  }, [theme]);

  // Listen to OS theme changes — only matters when we're in auto mode
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => {
      setSystemDark(e.matches);
      if (theme === "auto") apply("auto");
    };
    // Safari < 14 lacks addEventListener on MediaQueryList
    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  // Cycle: auto → light → dark → auto
  const toggle = useCallback(() => {
    setThemeState(t => t === "auto" ? "light" : t === "light" ? "dark" : "auto");
  }, []);

  const isDark =
    theme === "dark"
    || theme === "high-contrast"
    || (theme === "auto" && systemDark);

  return { theme, setTheme, toggle, isDark };
}

export function initTheme() {
  apply(getPreferred());
}
